/**
 * @name 可编辑表格组件
 * @author 吴迪 di.c.wu@pwc.com
 * @link https://4x.ant.design/components/table-cn/#API
 * @todo 暂时已知问题皆以解决，如有问题请联系作者或最后编辑的同学
 *
 * @desc 传入columns及dataSource即可轻松实现可编辑表格
 * @param tableProps 表格的属性（同antd-table）
 * 特殊说明：disabled+required 支持传入function，可以拿到当前行的数据
 * @param tableColumns 默认的表头配置（同antd-table的表头配置）
 * @param defaultDataSource 默认表格数据
 * @param needDelete 是否需要删除icon，默认true
 * @param disabledActions 是否禁用所有的操作（如：新增、删除、编辑）
 * @param extraBtns 额外的按钮组
 * @param afterAddRowFunc 增加行之后进行的操作-比如默认值之类的.在这个方法里可以拿到新增的那一行数据进行处理
 * @param beforeEditRowFunc 编辑行成功之前进行的操作-比如清空值之类的.在这个方法里可以拿到新增的那一行数据进行处理
 * @param afterDeleteRowFunc 删除行之后的回调-这个方法可以拿到删除后的剩余行数据进行处理
 * @param checkIsSuccessfulDeleted 判断删除行是否成功
 * @param dataChange 增、删、改数据之后的回调
 * @param changeCustmizeData 自定义改数据
 * @param afterCancel cancel按钮后的回调
 * @param saveData 点击Generate保存数据之后的回调，可以拿到整个table的数据
 *
 * @version 1.0.0
 * @createdTime 2023-4-18
 * @lastModifiedTime 2023-4-28
 * @lastModifiedUser 吴迪
 */

import React, {
  useMemo,
  useEffect,
  useState,
  useCallback,
  forwardRef,
  memo,
} from 'react';
import { Table, Form } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import moment from 'moment';
import { Button, FloatingActionButton } from 'appkit-react';
import { v4 as uuidv4 } from 'uuid';
import { EDIT_TABLE_COLUMNS_TYPE } from '@utils/enums';
import useTheme from '@hooks/useTheme';
import { EditableCell, EditableRow } from './components';
import addIcon from '@assets/imgs/add.png';
import light_addIcon from '@assets/imgs/light_add.png';
import deleteIcon from '@assets/imgs/cancel.png';
import deleteLightIcon from '@assets/imgs/cancel_light.png';
import _ from 'lodash';
import { handleEditTableData, returnPureData } from './dataProcessors';

import './index.scss';

export const EditableContext = React.createContext(null);

const Index = (
  {
    tableProps = {},
    tableColumns = [],
    defaultDataSource = [],
    needDelete = true,
    disabledActions = false,
    extraBtns = null,
    afterAddRowFunc = (newItem) => {},
    afterDeleteRowFunc = (newItem) => {},
    checkIsSuccessfulDeleted = (newItem) => true,
    beforeEditRowFunc = (newItem) => {},
    dataChange = (newData) => {},
    saveData = (resData) => {},
    toggleEdit = () => {},
    changeCustmizeData = () => {},
    afterCancel = () => {},
  },
  editableRowRef,
) => {
  const { theme } = useTheme();
  const isLightMode = theme === 'light';

  const [form] = Form.useForm();
  // 控制view模式与edit模式的,true：edit模式，false：view模式
  const [isEditMode, setIsEditMode] = useState(false);
  // 初始数据
  const initData = defaultDataSource.map((item, index) => {
    const newItem = { ...item };
    if (index !== 0) {
      delete item.tagA;
      delete item.tagB;
      delete item.tagC;
      delete item.tagD;
    }
    Object.keys(item).forEach((key) => {
      if (key === 'id') {
        newItem.id = item.id;
      } else {
        newItem[`${key}_${item.id}`] = item[key];
      }
    });
    return newItem;
  });

  const [tableDataSource, setTableDataSource] = useState(initData);

  useEffect(() => {
    setTableDataSource(initData);
  }, [JSON.stringify(initData)]);

  // 增删的时候通过该方法改数据，改当前组件使用的data以及暴露给父组件的data
  const changeData = (data) => {
    const newData = returnPureData(data);
    dataChange(newData);
    // 这里使用者可能需要改这个data，所以改完之后我再进行一次数据处理
    setTableDataSource(
      handleEditTableData({
        formVal: form.getFieldsValue(),
        tableData: newData,
      }),
    );
  };

  // 添加每一项
  const handleAdd = async () => {
    const id = uuidv4();
    const newData = { id };
    afterAddRowFunc(newData);
    changeData(
      handleEditTableData({
        formVal: form.getFieldsValue(),
        tableData: [...tableDataSource, newData],
      }),
    );
    return id;
  };

  // 删除
  const handleDelete = async (id) => {
    if (checkIsSuccessfulDeleted(id)) {
      const newData = tableDataSource.filter((item) => item.id !== id);
      afterDeleteRowFunc(newData);
      changeData(
        handleEditTableData({
          formVal: form.getFieldsValue(),
          tableData: newData,
        }),
      );
    }
  };

  // 保存数据 - 失焦即调
  const handleSave = useCallback(
    async (row, needRefreshTableData) => {
      const newData = [...tableDataSource];
      const index = newData.findIndex((item) => row.id === item.id);
      const curItem = newData[index];
      // console.log('保存curItem：', curItem);
      if (_.isEqual(row, curItem)) {
        return console.log('没有变化');
      }
      newData.splice(index, 1, {
        ...curItem,
        ...row,
      });
      // console.log('?????: ', form.getFieldsValue(), newData);
      const resData = handleEditTableData({
        formVal: form.getFieldsValue(),
        tableData: newData,
      });
      // console.log('resData: ', resData);
      // 在这里拿到处理过的数据之后这个数据才是正确的，所以在这里调before
      // 否则使用的地方拿到的数据是有问题的
      beforeEditRowFunc(resData[index]);

      if (needRefreshTableData) {
        changeData(resData);
      } else {
        // START - 处理使用组件的地方改变数据的逻辑
        const newPureData = returnPureData(resData);
        dataChange(newPureData);
        // 最终要设置的form的值
        const resFormValue = {};
        newPureData.forEach((record) => {
          // 如果有的话，去把他的除了id和rowIndex的每一个key都循环处理
          if (record) {
            Object.keys(record).forEach((key) => {
              if (!['id', 'rowIndex'].includes(key)) {
                const dataIndex = `${key}_${record?.id}`;
                const currentKeyType = tableColumns.find(
                  (col) => col.dataIndex === key,
                )?.type;
                // 判断是不是日期组件，如果是的话需要单独处理它的value
                const isDatePicker =
                  currentKeyType === EDIT_TABLE_COLUMNS_TYPE.DATE_PICKER;
                resFormValue[dataIndex] =
                  isDatePicker && record[key]
                    ? moment(record[key])
                    : record[key];
                resFormValue[key] =
                  isDatePicker && record[key]
                    ? moment(record[key])
                    : record[key];
              }
            });
          }
        });
        form.setFieldsValue(resFormValue);
        // END - 处理使用组件的地方改变数据的逻辑
      }
    },
    [tableDataSource],
  );

  // 获取禁用的类名 - 如果不是禁用的就返回空字符串
  const getDisabledClassName = useMemo(
    () => (disabledActions ? 'disabled_style' : ''),
    [disabledActions],
  );

  useEffect(() => {
    toggleEdit(isEditMode);
  }, [isEditMode, toggleEdit]);

  // 判断是否可操作
  const actionWrapper = useCallback(
    (action) => {
      if (disabledActions) {
        return;
      }
      action && action();
    },
    [disabledActions],
  );

  // 处理表头数据-追加 序号列/删除列 且处理可编辑逻辑
  const columns = useMemo(() => {
    const newColumns = [...tableColumns];
    if (needDelete && isEditMode) {
      newColumns.push({
        title: '',
        dataIndex: 'operation',
        width: 40,
        render: (_, record) =>
          tableDataSource.length >= 1 ? (
            // <Popconfirm title="Sure to delete?" onConfirm={() => handleDelete(record.id)}>
            <div
              className={`${getDisabledClassName} delete_wrapper`}
              onClick={() => actionWrapper(() => handleDelete(record.id))}
            >
              <img
                src={isLightMode ? deleteLightIcon : deleteIcon}
                className={`${getDisabledClassName} delete_icon`}
              />
            </div>
          ) : // </Popconfirm>
          null,
      });
    }
    return newColumns.map((col) => {
      if (!col.editable) {
        return col;
      }
      return {
        ...col,
        onCell: (record) => ({
          record,
          type: col.type,
          compProps: col.compProps,
          required: col.required,
          editable: col.editable,
          dataIndex: col.dataIndex,
          title: col.title,
          alwaysEditMode: isEditMode,
          placeholder: col.placeholder,
          disabled: disabledActions,
          handleSave,
        }),
      };
    });
  }, [isEditMode, tableColumns, needDelete, disabledActions, tableDataSource]);

  const editableRow = ({ record, index, ...restProps }) => (
    <EditableRow {...restProps} />
  );
  const components = {
    body: {
      row: editableRow,
      cell: EditableCell,
    },
  };

  const renderBtnGroups = () =>
    isEditMode ? (
      <div className="btn-group">
        {extraBtns}
        <FloatingActionButton
          className="cancel-btn"
          style={{ marginRight: 6 }}
          onClick={() => {
            // 恢复title编辑之前的值
            form.resetFields();
            // 恢复没编辑之前的值
            setTableDataSource(initData);
            // 恢复view模式
            setIsEditMode(false);
            afterCancel();
          }}
        >
          Cancel
        </FloatingActionButton>
        <FloatingActionButton
          className="generate-btn"
          style={{ height: 24 }}
          onClick={async () => {
            await form?.validateFields();
            const newArr = handleEditTableData({
              formVal: form.getFieldsValue(),
              tableData: tableDataSource,
            });
            // 在这里把数据暴露出去方便各自调接口
            changeCustmizeData(newArr);
            saveData(returnPureData(newArr));
            changeData(newArr);
            setIsEditMode(false);
          }}
        >
          Generate
        </FloatingActionButton>
      </div>
    ) : (
      <Button
        className="btn role-control edit-btn"
        style={{ height: 26 }}
        onClick={() => setIsEditMode(true)}
      >
        Edit
      </Button>
    );

  // console.log('tableDataSource: ', tableDataSource);
  return (
    <EditableContext.Provider value={form}>
      <div className="pwc_editor_table_wrapper">
        <div className="pwc_editor_table_header">
          <span />
          {renderBtnGroups()}
        </div>
        <Form
          ref={editableRowRef}
          form={form}
          component={false}
          autoComplete="off"
        >
          <Table
            components={components}
            rowClassName={() => 'editable-row'}
            bordered
            dataSource={tableDataSource}
            columns={columns}
            locale={{
              emptyText: () => (
                <div
                  className="edit-table-empty"
                  style={{ height: 200, paddingTop: 42 }}
                >
                  <InboxOutlined style={{ fontSize: 50 }} />
                  <p>Select “Edit Visual Data” button to get started</p>
                </div>
              ),
            }}
            {...tableProps}
          />
        </Form>
        {/* 目前快速点击add new 是有问题的，包括export之类的数据都会有些问题 */}
        {isEditMode && (
          <div
            className={`${getDisabledClassName} add_btn`}
            onClick={() => actionWrapper(handleAdd)}
          >
            <img src={isLightMode ? light_addIcon : addIcon} alt="add_icon" />
            <span>Add Category</span>
          </div>
        )}
      </div>
    </EditableContext.Provider>
  );
};

export default memo(forwardRef(Index));
